import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  TemplateRef,
  ViewChild
} from '@angular/core';
import {Backend, PaginatedResponse} from "@generated-backend-types";
import {ToastrService} from "ngx-toastr";
import {TranslatePipe, TranslateService} from "@ngx-translate/core";
import {Router, RouterModule} from "@angular/router";
import {TableFunctionList} from '../../../entities/table-function-list.entity';
import {TableDisplayConfig} from '../../../entities/table-display-config.entity';
import {UserSessionService} from '../../../services/user-session.service';
import {de} from 'date-fns/locale';
import {ClipboardModule} from 'ngx-clipboard';
import {
  ColumnMode,
  DataTableColumnDirective,
  DatatableComponent,
  NgxDatatableModule,
  SortType,
} from '@siemens/ngx-datatable';
import {environment} from '../../../../environments/environment';
import {ShouldShowColumnPipe} from '../../../pipes/should-show-column.pipe';
import {ParseIsoPipeModule} from 'ngx-date-fns';
import {FormatInTimeZonePipe} from '../../../pipes/format-in-time-zone.pipe';
import {CustomTableFunctionTypeEnum} from '../../../enums/CustomTableFunctionTypeEnum';
import {
  DecimalPipe,
  JsonPipe, NgClass,
  NgForOf,
  NgIf,
  NgStyle,
  NgSwitch,
  NgSwitchCase,
  NgSwitchDefault,
  NgTemplateOutlet
} from '@angular/common';
import TableConfigColumnTypeEnum = Backend.App.Enums.TableConfigColumnTypeEnum;
import TableConfigColumn = Backend.App.Resources.Dashboard.Meta.TableConfigColumn;
import ProcessCategoryEnum = Backend.App.Enums.QualityManagement.ProcessCategoryEnum;
import {FileSizePipe} from '../../../pipes/filesize.pipe';
import {DateTime} from 'luxon';

@Component({
  selector: 'app-table-data[tableDisplayConfig][data]',
  templateUrl: './table-data.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterModule, TranslatePipe, NgTemplateOutlet, NgForOf, NgIf, DecimalPipe, NgSwitchCase, ClipboardModule, ShouldShowColumnPipe, NgSwitch, NgxDatatableModule, JsonPipe, ParseIsoPipeModule, FormatInTimeZonePipe, NgStyle, NgSwitchDefault, FileSizePipe, NgClass],
  standalone: true
})
export class TableDataComponent<T> {

  public readonly TableConfigColumnTypeEnum = TableConfigColumnTypeEnum;
  public sorts!: { prop: string; dir: "desc" | "asc"; }[];
  public sortedColumns: TableConfigColumn[] = [];
  public headerHeight = 50;
  public functionColumnTypeList: TableConfigColumnTypeEnum[] = [
    TableConfigColumnTypeEnum.INL_FUNC_INLINE_DOWNLOAD_AND_DELETE_BUTTON,
    TableConfigColumnTypeEnum.INL_FUNC_INLINE_EDIT_AND_DELETE_BUTTON,
    TableConfigColumnTypeEnum.INL_FUNC_INLINE_DELETE_BUTTON,
    TableConfigColumnTypeEnum.INL_FUNC_INLINE_DOWNLOAD_BUTTON,
  ];

  @ViewChild(DatatableComponent) public tableDataComponent?: DatatableComponent;
  @ViewChild(DataTableColumnDirective) public column?: DataTableColumnDirective;

  @Input() additionalButton: TemplateRef<unknown> | null = null;
  @Input() additionalButtonWidth: number = 120;
  @Input() additionalButtonHeaderText: string = ''

  @Input() data!: PaginatedResponse<T>;
  @Input() isLoading = true;
  @Input() routerLinkPrefix?: string = ''
  @Input() routerLinkSuffix?: string = ''
  @Input() routerLinkValue?: string;
  @Input() showEditLink = true;
  @Input() routerErrorText?: string;
  @Input() functionList?: TableFunctionList;
  @Input() isSubtable: boolean = false;
  @Output() pageChanged: EventEmitter<number> = new EventEmitter<number>()
  @Output() sorted: EventEmitter<string> = new EventEmitter<string>()
  public keyPressedShift = false;
  public locale = 'de';
  public dfnsLocale = de;
  @Input() public cellTemplate?: TemplateRef<unknown>
  protected readonly ColumnMode = ColumnMode;
  protected readonly environment = environment;
  protected readonly SortType = SortType;
  protected readonly CUSTOM_TABLE_FUNCTION_TYPES = CustomTableFunctionTypeEnum;
  protected readonly ProcessCategoryEnum = ProcessCategoryEnum;

  constructor(
    public readonly userSessionService: UserSessionService,
    private readonly cdr: ChangeDetectorRef,
    private readonly toastrService: ToastrService,
    private readonly translate: TranslateService,
    private readonly router: Router
  ) {
  }

  private _tableDisplayConfig!: TableDisplayConfig;

  // @HostListener('window:keydown.shift', ['$event'])
  // public shiftDown() {
  //   this.keyPressedShift = true;
  // }
  //
  // @HostListener('window:keyup.shift', ['$event'])
  // public shiftUp() {
  //   this.keyPressedShift = false;
  // }

  get tableDisplayConfig() {
    return this._tableDisplayConfig
  }

  @Input() set tableDisplayConfig(tableDisplayConfig: TableDisplayConfig) {
    this._tableDisplayConfig = tableDisplayConfig;
    this.sorts = this.renderSorts();
    this.sortedColumns = this.renderColumns();
  }

  public goToPage(page: number): void {
    this.pageChanged.next(page)
  }

  public renderSorts(): { prop: string, dir: 'desc' | 'asc' }[] {
    const queryParam = this.tableDisplayConfig.sorts;
    const sorts: { prop: string, dir: 'desc' | 'asc' }[] = [];

    const columns = queryParam.split(',');
    columns.forEach((column: string) => {
      let columnName = column;
      let isDescending = false;

      if (columnName.startsWith('-')) {
        isDescending = true;
        columnName = columnName.substring(1);
      }

      if (columnName !== '') {
        sorts.push({prop: columnName, dir: isDescending ? 'desc' : 'asc'});
      }
    });

    if (sorts.length === 0) {
      sorts.push({prop: 'updated_at', dir: 'desc'});
    }
    return sorts;
  }

  public sort(sorts: { prop: string, dir: 'desc' | 'asc' }[]) {
    let queryParam = '';
    sorts.forEach((sortProp) => {

      // prepend separator
      if (queryParam !== '') {
        queryParam += ',';
      }
      // prepend descending indicator
      if (sortProp.dir === 'desc') {
        queryParam += '-';
      }
      queryParam += sortProp.prop;
    });

    if (queryParam === '' && this.tableDisplayConfig.tableConfig && Object.keys(this.tableDisplayConfig.tableConfig.columns).includes('updated_at')) {
      queryParam = '-updated_at'
    }
    this.sorted.emit(queryParam);
  }

  public rowIdentity(row: BaseResource): string {
    return row.id;
  }

  public renderColumns(): TableConfigColumn[] {

    const cols: TableConfigColumn[] = JSON.parse(JSON.stringify(this.tableDisplayConfig.tableConfig?.columns ?? []));
    cols.sort((c, d) => {
      return (this.tableDisplayConfig.column_order?.split(',') ?? []).findIndex(co => co === c.name) - (this.tableDisplayConfig.column_order?.split(',') ?? []).findIndex(co => co === d.name)
    });

    return cols;
  }

  public copied(value: string) {
    this.toastrService.info(value, this.translate.instant('table.idCopied'))
  }

  public isArray(value: unknown): boolean {
    return Array.isArray(value);
  }

  public shouldShowColumn(col: TableConfigColumn): boolean {
    return !this.tableDisplayConfig.hidden_columns?.includes(col.name) && col.visible;
  }

  public render() {
    this.tableDataComponent?.recalculate();
  }

  public showRouterError(event: Event): void {
    event.preventDefault();
    this.toastrService.error(this.routerErrorText)
  }

  public getProcessCategoryStyle(category: ProcessCategoryEnum) {
    switch (category) {
      case ProcessCategoryEnum.CORE:
        return {'background-color': '#DCFCE7', 'color': '#016630'};

      case ProcessCategoryEnum.LEADERSHIP:
        return {'background-color': '#E6F4F9', 'color': '#4AA9D4'};

      case ProcessCategoryEnum.SUPPORT:
        return {'background-color': '#FFEDD4', 'color': '#9F2D00'};
    }
  }

  protected readonly DateTime = DateTime;
}
